import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';

function CaseFilterRules({ showToast }) {
  const navigate = useNavigate();
  const { 
    caseFilterRules = [], 
    addCaseFilterRule, 
    updateCaseFilterRule, 
    deleteCaseFilterRule, 
    customerGroups,
    reapplyAllFilterRules,
    warnings = [],
    cases = []
  } = useData();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('rules');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    riskLevels: [], // ['high', 'medium', 'low']
    customerTypes: [], // ['Cá nhân', 'Doanh nghiệp']
    customerGroups: [], // Array of group IDs
    isActive: true,
    matchCondition: 'any' // 'any' or 'all'
  });
  
  const handleOpenModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({ ...rule });
    } else {
      setEditingRule(null);
      setFormData({
        name: '',
        description: '',
        riskLevels: [],
        customerTypes: [],
        customerGroups: [],
        isActive: true,
        matchCondition: 'any'
      });
    }
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      riskLevels: [],
      customerTypes: [],
      customerGroups: [],
      isActive: true,
      matchCondition: 'any'
    });
  };
  
  const handleCheckboxChange = (field, value) => {
    const current = formData[field] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setFormData({ ...formData, [field]: updated });
  };
  
  const handleSelectAll = (field, allValues) => {
    const current = formData[field] || [];
    if (current.length === allValues.length) {
      // Unselect all
      setFormData({ ...formData, [field]: [] });
    } else {
      // Select all
      setFormData({ ...formData, [field]: allValues });
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name.trim()) {
      alert('Vui lòng nhập tên rule!');
      return;
    }
    
    if (formData.riskLevels.length === 0 && formData.customerTypes.length === 0 && formData.customerGroups.length === 0) {
      alert('Vui lòng chọn ít nhất một điều kiện!');
      return;
    }
    
    const ruleData = {
      ...formData,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.username || 'Current User'
    };
    
    if (editingRule) {
      // Update
      updateCaseFilterRule({ ...ruleData, id: editingRule.id });
      if (ruleData.isActive) {
        showToast && showToast('✅ Đã cập nhật rule và chạy lại filter!', 'success');
        setActiveTab('autoCases');
      } else {
        showToast && showToast('✅ Đã cập nhật rule thành công!', 'success');
      }
    } else {
      // Create
      addCaseFilterRule({
        ...ruleData,
        id: `RULE-${Date.now()}`,
        createdAt: new Date().toISOString(),
        createdBy: user?.username || 'Current User'
      });
      if (ruleData.isActive) {
        showToast && showToast('✅ Đã tạo rule và chạy lại filter!', 'success');
        setActiveTab('autoCases');
        setTimeout(() => {
          navigate('/case-management');
        }, 800);
      } else {
        showToast && showToast('✅ Đã tạo rule thành công!', 'success');
      }
    }
    
    handleCloseModal();
  };
  
  const handleDelete = (rule) => {
    if (window.confirm(`Bạn có chắc muốn xóa rule "${rule.name}"?`)) {
      deleteCaseFilterRule(rule.id);
      showToast && showToast('✅ Đã xóa rule!', 'success');
    }
  };
  
  const handleToggleActive = (rule) => {
    const newActiveState = !rule.isActive;
    updateCaseFilterRule({ ...rule, isActive: newActiveState });
    
    if (newActiveState) {
      showToast && showToast('✅ Rule đã kích hoạt và đang chạy filter!', 'success');
    } else {
      showToast && showToast('Rule đã tắt!', 'success');
    }
  };
  
  const getRiskLevelBadge = (level) => {
    const badges = {
      high: { label: 'Cao', color: 'danger' },
      medium: { label: 'Trung bình', color: 'warning' },
      low: { label: 'Thấp', color: 'info' }
    };
    return badges[level] || badges.low;
  };

  const autoCreatedCases = useMemo(() => {
    return (cases || [])
      .filter(c => c.ruleId && c.isManual === false)
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  }, [cases]);

  const getRuleName = (caseItem) => {
    if (caseItem.ruleName) return caseItem.ruleName;
    const rule = caseFilterRules.find(r => r.id === caseItem.ruleId);
    return rule?.name || caseItem.ruleId || '-';
  };
  
  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="page-header-sticky">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2 className="mb-1">
              <i className="bi bi-funnel me-2"></i>
              Cấu hình Case Filter Rules
            </h2>
            <p className="text-muted mb-0">Quản lý rules để tự động filter warnings vào danh sách Case</p>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-success" 
              onClick={() => {
                reapplyAllFilterRules();
                showToast && showToast('🔄 Đã chạy lại filter cho tất cả warnings!', 'success');
                setActiveTab('autoCases');
              }}
              title="Chạy lại filter cho tất cả warnings hiện có"
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Chạy lại Filter
            </button>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Tạo Rule Mới
            </button>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">Tổng số rules</small>
                  <h3 className="mb-0">{caseFilterRules.length}</h3>
                </div>
                <i className="bi bi-funnel-fill fs-1 text-primary opacity-25"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">Đang hoạt động</small>
                  <h3 className="mb-0 text-success">{caseFilterRules.filter(r => r.isActive).length}</h3>
                </div>
                <i className="bi bi-check-circle-fill fs-1 text-success opacity-25"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">Đã tắt</small>
                  <h3 className="mb-0 text-secondary">{caseFilterRules.filter(r => !r.isActive).length}</h3>
                </div>
                <i className="bi bi-x-circle-fill fs-1 text-secondary opacity-25"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">Warnings chưa có case</small>
                  <h3 className="mb-0 text-warning">{warnings.filter(w => 
                    w.status !== 'resolved' && 
                    !cases.some(c => c.warningId === w.id)
                  ).length}</h3>
                </div>
                <i className="bi bi-exclamation-triangle-fill fs-1 text-warning opacity-25"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'rules' ? 'active' : ''}`}
            onClick={() => setActiveTab('rules')}
          >
            <i className="bi bi-funnel me-1"></i>
            Case Filter Rules ({caseFilterRules.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'autoCases' ? 'active' : ''}`}
            onClick={() => setActiveTab('autoCases')}
          >
            <i className="bi bi-folder-check me-1"></i>
            Case tạo từ Rule ({autoCreatedCases.length})
          </button>
        </li>
      </ul>
      
      {/* Rules List */}
      {activeTab === 'rules' && (
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <h5 className="mb-0">Danh sách Rules</h5>
        </div>
        <div className="card-body">
          {caseFilterRules.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-funnel fs-1 d-block mb-2"></i>
              <p>Chưa có rule nào được cấu hình</p>
              <button className="btn btn-primary btn-sm" onClick={() => handleOpenModal()}>
                <i className="bi bi-plus-circle me-1"></i>
                Tạo Rule Đầu Tiên
              </button>
            </div>
          ) : (
            <div className="row g-3">
              {caseFilterRules.map(rule => (
                <div key={rule.id} className="col-md-6">
                  <div className={`card h-100 ${rule.isActive ? 'border-success' : 'border-secondary'}`}>
                    <div className="card-body">
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h5 className="mb-1">{rule.name}</h5>
                          <p className="text-muted small mb-0">{rule.description || 'Không có mô tả'}</p>
                        </div>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={rule.isActive}
                            onChange={() => handleToggleActive(rule)}
                            title={rule.isActive ? 'Tắt rule' : 'Kích hoạt rule'}
                          />
                        </div>
                      </div>
                      
                      {/* Conditions */}
                      <div className="mb-3">
                        <small className="text-muted d-block mb-2">
                          <i className="bi bi-gear me-1"></i>
                          Điều kiện: <strong>{rule.matchCondition === 'any' ? 'Đáp ứng BẤT KỲ' : 'Đáp ứng TẤT CẢ'}</strong>
                        </small>
                        
                        {/* Risk Levels */}
                        {rule.riskLevels && rule.riskLevels.length > 0 && (
                          <div className="mb-2">
                            <small className="text-muted d-block">Mức độ rủi ro:</small>
                            <div className="d-flex gap-1 flex-wrap">
                              {rule.riskLevels.map(level => {
                                const badge = getRiskLevelBadge(level);
                                return (
                                  <span key={level} className={`badge bg-${badge.color}`}>
                                    {badge.label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Customer Types */}
                        {rule.customerTypes && rule.customerTypes.length > 0 && (
                          <div className="mb-2">
                            <small className="text-muted d-block">Loại khách hàng:</small>
                            <div className="d-flex gap-1 flex-wrap">
                              {rule.customerTypes.map(type => (
                                <span key={type} className="badge bg-primary">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Customer Groups */}
                        {rule.customerGroups && rule.customerGroups.length > 0 && (
                          <div className="mb-2">
                            <small className="text-muted d-block">Nhóm khách hàng:</small>
                            <div className="d-flex gap-1 flex-wrap">
                              {rule.customerGroups.map(groupId => {
                                const group = customerGroups.find(g => g.id === groupId);
                                return (
                                  <span key={groupId} className="badge bg-secondary">
                                    {group?.name || groupId}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Metadata */}
                      <div className="mb-3">
                        <small className="text-muted d-block">
                          <i className="bi bi-clock me-1"></i>
                          Tạo: {new Date(rule.createdAt).toLocaleString('vi-VN')} bởi {rule.createdBy}
                        </small>
                        {rule.updatedAt && (
                          <small className="text-muted d-block">
                            <i className="bi bi-arrow-repeat me-1"></i>
                            Cập nhật: {new Date(rule.updatedAt).toLocaleString('vi-VN')}
                          </small>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary flex-fill"
                          onClick={() => handleOpenModal(rule)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Sửa
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(rule)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Auto Cases Tracking Tab */}
      {activeTab === 'autoCases' && (
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Theo dõi Case được tạo tự động từ Rule</h5>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => navigate('/case-management')}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>
            Mở Form Case
          </button>
        </div>
        <div className="card-body">
          {autoCreatedCases.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              <p className="mb-1">Chưa có case nào được tạo từ rule</p>
              <small>Tạo/kích hoạt rule và bấm "Chạy lại Filter" để sinh case tự động</small>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Khách hàng</th>
                    <th>Mã cảnh báo</th>
                    <th>Rule tạo case</th>
                    <th>Trạng thái</th>
                    <th>Thời gian tạo</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {autoCreatedCases.map(caseItem => (
                    <tr key={caseItem.id}>
                      <td><strong>{caseItem.id}</strong></td>
                      <td>
                        <div>{caseItem.customerName}</div>
                        <small className="text-muted">{caseItem.customerId}</small>
                      </td>
                      <td>
                        <span className="badge bg-warning text-dark">{caseItem.warningCode || '-'}</span>
                      </td>
                      <td>
                        <span className="badge bg-info text-dark">{getRuleName(caseItem)}</span>
                      </td>
                      <td>
                        <span className={`badge bg-${caseItem.status === 'completed' ? 'success' : caseItem.status === 'in_progress' ? 'primary' : 'secondary'}`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td>
                        <small>{new Date(caseItem.createdAt).toLocaleString('vi-VN')}</small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate('/case-management')}
                          title="Mở form Case"
                        >
                          <i className="bi bi-eye me-1"></i>
                          Xem trong Form Case
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}
      
      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRule ? 'Chỉnh sửa Rule' : 'Tạo Rule Mới'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {/* Name */}
                  <div className="mb-3">
                    <label className="form-label">Tên Rule <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="VD: Auto filter high risk warnings"
                      required
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="mb-3">
                    <label className="form-label">Mô tả</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Mô tả ngắn về rule này..."
                    />
                  </div>
                  
                  {/* Match Condition */}
                  <div className="mb-4">
                    <label className="form-label">Điều kiện khớp</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="matchCondition"
                        id="matchAny"
                        value="any"
                        checked={formData.matchCondition === 'any'}
                        onChange={(e) => setFormData({...formData, matchCondition: e.target.value})}
                      />
                      <label className="form-check-label" htmlFor="matchAny">
                        <strong>Đáp ứng BẤT KỲ</strong> điều kiện nào dưới đây (OR)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="matchCondition"
                        id="matchAll"
                        value="all"
                        checked={formData.matchCondition === 'all'}
                        onChange={(e) => setFormData({...formData, matchCondition: e.target.value})}
                      />
                      <label className="form-check-label" htmlFor="matchAll">
                        <strong>Đáp ứng TẤT CẢ</strong> điều kiện dưới đây (AND)
                      </label>
                    </div>
                  </div>
                  
                  {/* Risk Levels */}
                  <div className="mb-3">
                    <label className="form-label">
                      Mức độ rủi ro
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => handleSelectAll('riskLevels', ['high', 'medium', 'low'])}
                      >
                        {formData.riskLevels.length === 3 ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                      </button>
                    </label>
                    <div className="border rounded p-3">
                      {['high', 'medium', 'low'].map(level => {
                        const badge = getRiskLevelBadge(level);
                        return (
                          <div key={level} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`risk-${level}`}
                              checked={formData.riskLevels.includes(level)}
                              onChange={() => handleCheckboxChange('riskLevels', level)}
                            />
                            <label className="form-check-label" htmlFor={`risk-${level}`}>
                              <span className={`badge bg-${badge.color} me-2`}>{badge.label}</span>
                              ({level})
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Customer Types */}
                  <div className="mb-3">
                    <label className="form-label">
                      Loại khách hàng
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => handleSelectAll('customerTypes', ['Cá nhân', 'Doanh nghiệp'])}
                      >
                        {formData.customerTypes.length === 2 ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                      </button>
                    </label>
                    <div className="border rounded p-3">
                      {['Cá nhân', 'Doanh nghiệp'].map(type => (
                        <div key={type} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`type-${type}`}
                            checked={formData.customerTypes.includes(type)}
                            onChange={() => handleCheckboxChange('customerTypes', type)}
                          />
                          <label className="form-check-label" htmlFor={`type-${type}`}>
                            {type}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Customer Groups */}
                  <div className="mb-3">
                    <label className="form-label">
                      Nhóm khách hàng
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => handleSelectAll('customerGroups', customerGroups.map(g => g.id))}
                      >
                        {formData.customerGroups.length === customerGroups.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                      </button>
                    </label>
                    <div className="border rounded p-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                      {customerGroups.map(group => (
                        <div key={group.id} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`group-${group.id}`}
                            checked={formData.customerGroups.includes(group.id)}
                            onChange={() => handleCheckboxChange('customerGroups', group.id)}
                          />
                          <label className="form-check-label" htmlFor={`group-${group.id}`}>
                            {group.name}
                            {group.description && (
                              <small className="text-muted d-block">{group.description}</small>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Active Status */}
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Kích hoạt rule ngay sau khi tạo
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Hủy
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-check-circle me-2"></i>
                    {editingRule ? 'Cập nhật' : 'Tạo Rule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseFilterRules;
